import { ActivatedRouteSnapshot, Route, RouterStateSnapshot, Routes } from "@angular/router";
import { EMPLOYEE_ROUTES } from "../features/employee/constants/routes.constant";
import { EmployeeModule } from "../features/employee/employee.module";
import { RoutePlaceholderComponent } from "@core/components/route-placeholder.component";
import { CUSTOMER_ROUTE_NAMES } from "./route-names.const";
import { CUSTOMER_ROUTES_PARAM_MAPS } from "./customer-routes-param-maps.const";
import { CustomerToastService } from "../components/pages/customer-tabs/services/customer-toast.service";
import { WILDCARD_ROUTE } from "@shared/common/routes.common";
import { ExtjsWildcardRouteComponent } from "@core/components/pages/extjs/extjs-wildcard-route.component";
import { hasPathInSegment } from "@core/constants/routes.constant";
import { inject } from "@angular/core";
import { ExtjsService } from "@core/services/extjs/extjs.service";
import { FormStateService, pendingChangesGuard } from "@core/services/form-state.service";
import { permissionsGuard } from "@shared/guards/permissions.guard";
import { CUSTOMER_ROLE_RESOURCE, CUSTOMER_ROLES } from "@shared/types/roles/customer-roles-types";

export const DEACTIVATE_PREFIX: string = "deactivated_";

const canActivateFn = (): boolean => false;
const deactivate = (route: Route): Route => {
  route.path = `${ DEACTIVATE_PREFIX }${ route.path }`;
  route.canActivate
    ? route.canActivate.push(canActivateFn)
    : route.canActivate = [ canActivateFn ];

  return route;
};
const TRANSLATION_SCOPE: string = "feature/customer-routes";

export const CUSTOMER_ROUTES: Routes = [
  {
    path: "",
    // eslint-disable-next-line @tseslint/typedef
    loadComponent: async () => await import("@features/customer/components/pages/customer-overview/customer-overview.component").then((m) => m.CustomerOverview)
  },
  deactivate({
    path: "new/legacy",
    component: RoutePlaceholderComponent,
    data: {
      context: {
        breadcrumb: {
          "static": {
            translationInfo: {
              defaultText: "Unternehmen neu anlegen",
              token: "breadcrumb-label-new-customer",
              scope: TRANSLATION_SCOPE
            }
          }
        }
      }
    }
  }),
  {
    path: `:${ CUSTOMER_ROUTES_PARAM_MAPS.customerNumber }`,
    data: {
      context: {
        breadcrumb: {
          "static": {
            translationInfo: {
              defaultText: "Unternehmen neu anlegen",
              token: "breadcrumb-label-new-customer",
              scope: TRANSLATION_SCOPE
            }
          },
          staticCondition: (paramMap: Map<string, string>): boolean => paramMap.get(CUSTOMER_ROUTES_PARAM_MAPS.customerNumber) === "new",
          apiPath: `api/customers/{${ CUSTOMER_ROUTES_PARAM_MAPS.customerNumber }}`
        },
        sidebar: { apiPath: `api/menu/customer/{${ CUSTOMER_ROUTES_PARAM_MAPS.customerNumber }}` }
      }
    },
    children: [
      {
        path: "",
        redirectTo: CUSTOMER_ROUTE_NAMES.settings,
        pathMatch: "full"
      },

      {
        path: CUSTOMER_ROUTE_NAMES.settings,
        // eslint-disable-next-line @tseslint/typedef
        loadComponent: async () => await import("@features/customer/components/pages/customer-tabs/customer-tabs.component").then((c) => c.CustomerTabsComponent),
        providers: [ FormStateService, CustomerToastService ],
        children: [
          {
            path: "",
            pathMatch: "full",
            redirectTo: CUSTOMER_ROUTE_NAMES.details
          },
          {
            path: CUSTOMER_ROUTE_NAMES.details,
            // eslint-disable-next-line @tseslint/typedef
            loadComponent: async () => await import("@features/customer/components/pages/customer-tabs/components/customer-details/customer-details.component").then((c) => c.CustomerDetailsComponent),
            data: {
              context: {
                breadcrumb: {
                  "static": {
                    translationInfo: {
                      defaultText: "Details",
                      token: "breadcrumb-label-details",
                      scope: TRANSLATION_SCOPE
                    }
                  }
                }
              }
            }
          },
          deactivate({
            path: CUSTOMER_ROUTE_NAMES.documents,
            component: RoutePlaceholderComponent,
            data: {
              context: {
                breadcrumb: {
                  "static": {
                    translationInfo: {
                      defaultText: "Dokumente",
                      token: "breadcrumb-label-documents",
                      scope: TRANSLATION_SCOPE
                    }
                  }
                }
              }
            }
          }),
          deactivate({
            path: CUSTOMER_ROUTE_NAMES.personsOfContact,
            component: RoutePlaceholderComponent,
            data: {
              context: {
                breadcrumb: {
                  "static": {
                    translationInfo: {
                      defaultText: "Ansprechpartner",
                      token: "breadcrumb-label-persons-of-contact",
                      scope: TRANSLATION_SCOPE
                    }
                  }
                }
              }
            }
          }),
          {
            path: CUSTOMER_ROUTE_NAMES.systemStatus,
            // eslint-disable-next-line @tseslint/typedef
            loadComponent: async () => await import("@features/customer/components/pages/customer-tabs/components/customer-system-status/customer-system-status.component").then((c) => c.CustomerSystemStatusComponent),
            canDeactivate: [ pendingChangesGuard ],
            canActivate: [ permissionsGuard([
              {
                resource: CUSTOMER_ROLE_RESOURCE,
                role: CUSTOMER_ROLES.admin
              }
            ]) ],
            data: {
              context: {
                breadcrumb: {
                  "static": {
                    translationInfo: {
                      defaultText: "Systemstatus",
                      token: "breadcrumb-label-system-status",
                      scope: TRANSLATION_SCOPE
                    }
                  }
                }
              }
            }
          },
          {
            path: WILDCARD_ROUTE,
            component: ExtjsWildcardRouteComponent,
            canActivate: [
              (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
                inject(ExtjsService).scope.set("customer-taps");

                return !hasPathInSegment("extjs")(route, state);
              }
            ]
          },
          deactivate({
            path: "created",
            component: RoutePlaceholderComponent
          })
        ]
      },
      deactivate({
        path: CUSTOMER_ROUTE_NAMES.employees,
        loadChildren: async (): Promise<typeof EmployeeModule> =>
          (await import("../features/employee/employee.module")).EmployeeModule,
        data: {
          routes: () => EMPLOYEE_ROUTES,
          context: {
            breadcrumb: {
              "static": {
                translationInfo: {
                  defaultText: "Mitarbeiter",
                  token: "breadcrumb-label-employees",
                  scope: TRANSLATION_SCOPE
                }
              }
            }
          }
        }
      }),
      deactivate({
        path: CUSTOMER_ROUTE_NAMES.projects,
        component: RoutePlaceholderComponent,
        data: {
          leaf: true,
          context: {
            breadcrumb: {
              "static": {
                translationInfo: {
                  defaultText: "Projektebeteiligungen",
                  token: "breadcrumb-label-project-participations",
                  scope: TRANSLATION_SCOPE
                }
              }
            }
          }
        }
      }),
      {
        path: WILDCARD_ROUTE,
        component: ExtjsWildcardRouteComponent,
        canActivate: [
          (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
            inject(ExtjsService).scope.set("core");

            return !hasPathInSegment("extjs")(route, state);
          }
        ]
      }
    ]
  }
];
